import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { CustomObject } from '../../../types/data';
import { useTexture, useStorySlides, useMedia } from '../../../hooks';
import Button from '../../../components/xr/button';
import Image from '../../../components/xr/image';

import storyBg from '../../../assets/images/xr/story_bg.png';
// import storyBtnLeft from '../../../assets/images/xr/icons/story_btn_left.png';
// import storyBtnRight from '../../../assets/images/xr/icons/story_btn_right.png';

import storyImageZoomBtn from '../../../assets/images/xr/icons/story-image-zoom.png';
import storyImageZoomActiveBtn from '../../../assets/images/xr/icons/story-image-zoom-active.png';

import StoryText from './text';
import StoryProgressBar from './progress-bar';
import StoryActions from './actions';
import { MediaEvents, MediaType } from '../../../common/media';
import GatewayService from '../../../services/gateway-service';
import { ACTION_PLAY_EMBED_VIDEO, STORY_ENDED } from '../../../common/constants/gateway-events';

import { context } from '../../../config/context';
import { Euler, MathUtils } from 'three';
import ZoomedImage from './zoomed-image';
import PreloadService from '../../../services/preload-service';
import ImageButton from '../../../components/xr/image-buton';

const { degToRad } = MathUtils;

const arrowWidth = 0.12;
const arrowHeight = 0.5;
const arrowX = 0.25;

type StoryWindowProps = {
  data: CustomObject;
  subtitles: any[];
  onClose: () => void;
};

const StoryWindow = ({ data, subtitles, onClose }: StoryWindowProps) => {
  const media = useMedia(data.id, MediaType.Audio);
  const { texture: storyBgTxt } = useTexture(storyBg);

  const { texture: storyImageZoomTxt } = useTexture(storyImageZoomBtn);
  const { texture: storyImageZoomActiveTxt } = useTexture(storyImageZoomActiveBtn);

  const { controls } = useContext(context);

  const slides = useStorySlides(data, subtitles);
  const [{ activeSlideIndex, isPlaying, isZoomed }, setState] = useState({
    activeSlideIndex: 0,
    isPlaying: false,
    isZoomed: false,
  });

  const highlightCont = useRef<any>();
  const activeBar = useRef<any>();

  const isPlayable = !!data.audio_url;
  const activeSlide = slides[activeSlideIndex];
  const duration = activeSlide ? activeSlide.end - activeSlide.start : 0;

  const handleSlideChange = useCallback(
    (index: number) => {
      index = Math.max(0, index);
      index = Math.min(slides.length - 1, index);

      if (index !== activeSlideIndex) {
        if (index > 0) {
          const slide = slides[index];if(slide) {
            media.setCurrentTime(slide.start);
          }
        } else {
          media.pause();
        }
      }

      setState(st => ({ ...st, activeSlideIndex: index }));
    },
    [slides, activeSlideIndex, media]
  );

  const handleToggleAudio = useCallback(() => {
    if (media.isPaused()) {
      if (activeSlide && activeSlide.start >= 0) {
        media.setCurrentTime(activeSlide.start);

        media.once(MediaEvents.Play, () => activeBar.current?.stateReset());

        media.play();
      }
    } else {
      media.pause();
    }
  }, [activeSlide, media]);

  const handleToggleImageZoom = useCallback(() => {
    if (!activeBar.current) return;

    if (!isZoomed) {
      activeBar.current.statePause();
      setState(st => ({ ...st, isZoomed: true }));
      media.pause();
    } else {
      activeBar.current.statePlay();
      setState(st => ({ ...st, isZoomed: false }));
    }
  }, [isZoomed, media]);

  const handleToggleEmbedVideoPlayer = useCallback(()=>{
    GatewayService.emit(ACTION_PLAY_EMBED_VIDEO,data.embed_video)
  },[data]);

  useEffect(() => {
    const handleChangePlaying = () => setState(st => ({ ...st, isPlaying: !media.isPaused() }));

    const handleEnd = () => {
      setState(st => ({ ...st, isPlaying: false }));
      GatewayService.emit(STORY_ENDED, data);
    };

    const handleTimeupdate = ({ currentTime }: { currentTime: number }) => {
      if (!media.isPaused() && activeSlide && subtitles.length && highlightCont.current) {
        let currentRow = 0;
        const highlightWords: string[] = [];

        for (const fragment of subtitles) {
          const start = +fragment.begin;
          const end = +fragment.end;

          if (end > activeSlide.end || start > currentTime) {
            break;
          }

          if (start >= activeSlide.start) {
            let row: string = highlightWords[currentRow];

            if (!row) row = '';

            row += ' ' + fragment.lines[0];

            highlightWords[currentRow] = row.trim();
            if(highlightWords[currentRow].startsWith("of")){
              highlightWords[currentRow] = highlightWords[currentRow].substr(2);
            }

            if (row.trim() === activeSlide.text[currentRow].trim()) currentRow += 1;
          }
        }

        highlightCont.current.text = highlightWords.join('\n');
      }
    };

    media.on(MediaEvents.Change_Playing, handleChangePlaying);
    media.on(MediaEvents.Ended, handleEnd);
    media.on(MediaEvents.Timeupdate, handleTimeupdate);

    return () => {
      media.off(MediaEvents.Change_Playing, handleChangePlaying);
      media.off(MediaEvents.Ended, handleEnd);
      media.off(MediaEvents.Timeupdate, handleTimeupdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSlide, data.id, media, slides, subtitles, subtitles.length]);

  useEffect(() => {
    // load thumbnails
    data.images.forEach((img: any) => {
      PreloadService.loadImage(img.vr_preview_url);
    });
    // load main images
    data.images.forEach((img: any) => {
      PreloadService.loadImage(img.image_url);
    });

    controls?.current?.lookAtWithAnimation(new Euler(0, degToRad(-data.ath), 0, 'YXZ'));

    if (!isPlayable) return;

    media.setResource({ src: data.audio_url });

    return () => media.destroy();
  }, [media, data.id, data.audio_url, controls, isPlayable, data.ath]);

  return (
    <group rotation={[0, degToRad(-data.ath), 0, 'YXZ']}>
      <group position={[0, 0, -1]}>
        <group>
          <mesh position={[0, 0, 0]} scale={[1.3, 1.3, 1.3]}>
            <planeBufferGeometry args={[0.517, 0.5]} attach="geometry" />
            <meshBasicMaterial attach="material" color="#ffffff" transparent opacity={0.9} map={storyBgTxt} />

            <group position={[0, 0.23, 0.001]}>
              <StoryProgressBar
                width={0.477}
                count={slides.length}
                active={activeSlideIndex}
                duration={duration * 1000}
                onComplete={() => handleSlideChange(activeSlideIndex + 1)}
                ref={activeBar}
              />
            </group>

            {activeSlide?.image && activeSlide?.image?.type !=='video' ? (
              <group position={[0, 0.066, 0.0001]}>
                <Image width={0.517} height={0.291} src={activeSlide.image.thumbnail} renderOrder={2} />

                <Button
                  onClick={handleToggleImageZoom}
                  position={[0.22, 0.11, 0.01]}
                  renderOrder={3}
                  defaultState={{
                    main: {
                      geometry: 'plane',
                      size: 0.04,
                      material: { transparent: true, opacity: 1, map: storyImageZoomTxt },
                    },
                  }}
                  hoverState={{
                    main: {
                      map: storyImageZoomActiveTxt,
                    },
                  }}
                />
              </group>
            ) : null}
            {activeSlide?.image?.type ==='video' ? (
              <group position={[0, 0.066, 0.0001]}>
                <ImageButton width={0.517} height={0.291} onClick={handleToggleEmbedVideoPlayer} src={activeSlide.image.thumbnail} renderOrder={2} />
              </group>
            ) : null}

            <StoryText
              value={activeSlide?.text?.join('\n')}
              highlightValue={!isPlaying ? activeSlide?.text?.join('\n') : undefined}
              isTitle={activeSlideIndex === 0}
              isBig={!activeSlide?.image}
              ref={highlightCont}
            />

              <group position={[0, 0, 0.01]}>
                <Button
                  onClick={() => handleSlideChange(activeSlideIndex - 1)}
                  position={[-arrowX, 0, 0]}
                  renderOrder={4}
                  defaultState={{
                    main: {
                      width: arrowWidth,
                      height: arrowHeight,
                      material: { transparent: true, opacity: 0 },
                    },
                  }}
                  hoverState={{
                    cursorClass: 'cursor-arrow-left',
                    main: {
                      scale: [1, 1, 1]
                    },
                  }}
                />

                <Button
                  onClick={() => handleSlideChange(activeSlideIndex + 1)}
                  position={[arrowX, 0, 0]}
                  renderOrder={4}
                  defaultState={{
                    main: {
                      width: arrowWidth,
                      height: arrowHeight,
                      material: { transparent: true, opacity: 0 },
                    },
                  }}
                  hoverState={{
                    cursorClass: 'cursor-arrow-right',
                    main: {
                      scale: [1, 1, 1]
                    },
                  }}
                />
              </group>
          </mesh>
          <StoryActions
            onClickClose={onClose}
            onToggleAudio={handleToggleAudio}
            onClickEdit={(e: any) => {}}
            onClickDelete={(e: any) => {}}
            showAudio={isPlayable}
            audioIconN={isPlaying ? 1 : 0}
            position={[0, -0.4, 0]}
          />
        </group>

        {isZoomed && activeSlide?.image ? (
          <group position={[0, 0, 0.02]}>
            <ZoomedImage src={activeSlide.image.original} onClose={handleToggleImageZoom} renderOrder={10} />
          </group>
        ) : null}
      </group>
    </group>
  );
};

export default StoryWindow;
